import { Prescriber } from '../../types'
import { PrescriberCreation } from '../../types/prescriber'

export type PrescriberInService = Omit<Prescriber, 'qualifications'> & { qualifications: string[] }

/**
 * A prescriber is only usable if it carries an `id` the platform can be asked
 * about. Every server response includes one, so this rejects corrupt or
 * hand-edited data rather than anything the API sends. A blank string is not an
 * identity either: it produces the same empty `Prescriber-Id` request header as
 * an absent one. Trimmed, to match the blank-qualification rule below.
 *
 * An empty value is not usable either, so `null`/`undefined` are accepted and
 * rejected rather than dereferenced: a wire response that omits the prescriber
 * entirely must normalise to `null`, not throw and fail the whole EHR launch.
 *
 * Typed loosely on purpose: it only inspects `id`, so it serves both the
 * UI-shape reader and `PrescriberInService`, whose `qualifications` is an array.
 */
const hasUsableId = (data: { id?: unknown } | null | undefined): boolean =>
  typeof data?.id === 'string' && data.id.trim().length > 0

/**
 * Joins the service shape (`string[]`) into the UI shape (`string`).
 *
 * Blank entries are dropped, matching the profile validation
 * (`validatePrescriberProfile`), the send guard and the simple-json backfill
 * (`LENGTH(TRIM(qualifications)) > 0`). A whitespace-only value is truthy, so
 * keeping it would let the send flow treat the prescriber as qualified while the
 * profile gate still reports the field as missing.
 */
const joinQualifications = (qualifications: unknown): string => {
  const entries = typeof qualifications === 'string' ? qualifications.split(',') : qualifications
  if (!Array.isArray(entries)) return ''

  return entries
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .join(',')
}

export const transformPrescriberForService = (data: Prescriber | PrescriberCreation) => {
  return {
    ...data,
    qualifications:
      data?.qualifications
        ?.split(',')
        .map((qualification) => qualification.trim())
        .filter((qualification) => qualification.length > 0) || [],
  }
}

/**
 * `qualifications` is a `simple-json` column, so a legacy row can hydrate it as
 * something other than the `string[]` the type promises: typeorm parses the column
 * with a bare `JSON.parse`, which turns a quoted value into a plain string. A string
 * is already the joined UI shape, and anything unusable is dropped instead of
 * throwing on `.join()`.
 *
 * `null` means the value is not a prescriber the app can act for - the same
 * fail-closed answer `normalizePersistedPrescriber` gives, because an id-less
 * prescriber cannot be asked about: `Prescriber-Id` goes out empty and the platform
 * silently treats the request as having no prescriber at all. Callers already hold
 * `null` as "no prescriber", so an unusable response lands in a state every reader
 * handles.
 */
export const transformPrescriberForUI = (data: PrescriberInService): Prescriber | null => {
  if (!hasUsableId(data)) return null

  const { qualifications, ...restData } = data

  const joined = joinQualifications(qualifications)

  if (joined.length > 0) {
    return {
      ...restData,
      qualifications: joined,
    }
  }

  return restData
}

type PersistedPrescriber = Partial<Prescriber> & { qualifications?: unknown }

/**
 * `localStorage['prescriber']` has been written in two shapes over time: the UI
 * shape (`qualifications` joined into a string) and the raw service shape
 * (`qualifications: string[]`, straight from the API). Normalise on read so
 * whichever writer touched the key last, Redux only ever hydrates the UI shape.
 *
 * A value with no usable `id` is discarded outright, the same answer the API
 * boundary gives: hydrating one would leave every request carrying an empty
 * `Prescriber-Id`, which the platform treats as no prescriber rather than an error.
 */
export const normalizePersistedPrescriber = (raw: unknown): Prescriber | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  if (!hasUsableId(raw)) return null

  const { qualifications, ...restData } = raw as PersistedPrescriber
  const joined = joinQualifications(qualifications)

  // Every value is coerced, including a string: the UI shape can still be blank or
  // padded. Nothing usable - absent, blank, or a type that is not a qualification -
  // drops the key rather than hydrating a value the send guard accepts but the
  // profile validation treats as empty.
  if (joined.length === 0) return restData as Prescriber

  return { ...restData, qualifications: joined } as Prescriber
}

/**
 * Reads the persisted prescriber defensively. The key can legitimately hold
 * `'undefined'`/`'null'` from older writers, and malformed JSON must not take
 * down whichever module happens to import the slice first.
 */
export const readPersistedPrescriber = (json: string | null): Prescriber | null => {
  if (!json || json === 'undefined' || json === 'null') return null

  try {
    return normalizePersistedPrescriber(JSON.parse(json))
  } catch {
    return null
  }
}

export const validatePrescriberProfile = (prescriber: Partial<Prescriber>): boolean => {
  const requiredFields: (keyof Prescriber)[] = [
    'givenNames',
    'familyName',
    'email',
    'prescriberNumber',
    'entityId',
    'prescriberType',
    'hpii',
    'hpiiStatus',
    'qualifications',
  ]

  return requiredFields.every((field) => {
    const value = prescriber[field]
    return value && (typeof value === 'string' ? value.trim() !== '' : true)
  })
}
